import { useState } from 'react';
import {
  DragDropContext,
  Draggable,
  DragStart,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'src/components/molecules';
import { EmptyState } from 'src/components/molecules/EmptyState/EmptyState';
import { PageHeaderWithActions } from 'src/components/molecules/PageHeaderWithActions/PageHeaderWithActions';
import { TagsSummary } from 'src/components/molecules/TagsSummary/TagsSummary';
import {
  SuggestedReading,
  TagsAuditLogTable,
  TagTypeItem,
} from 'src/components/organisms';
import {
  TagTypeType,
  useUpdateTagOrdersMutation,
  useUpdateTagTypeOrdersMutation,
} from 'src/graphql';
import { useIsLoading } from 'src/hooks';
import {
  getOrganisationTagTypes,
  setGlobalTagTypes,
} from 'src/reducers/Tags.slice';
import { tagsIntercomArticle } from 'src/utils/intercomArticles';
import { Loading } from '../Loading';
import {
  BodyContainer,
  EmptyStateWrapper,
  GridContainer,
  OrderInfo,
  OrderInfoContainer,
  OrderInfoSubtitle,
  OrderParagraph,
  PageContainer,
  PageHeaderContainer,
  TagContainer,
  TagsBody,
} from './Tags.styles';
import useFetchTagTypes from './hooks/useFetchTagTypes';
import { useTagsTypesCoordinates } from './hooks/useTagsCoordinates';
export const Tags = () => {
  const { isTagTypesLoading, invalidateQuery } = useFetchTagTypes();
  const dispatch = useDispatch();

  const tagTypes = useSelector(getOrganisationTagTypes);

  const noTagTypes = !tagTypes || tagTypes?.length === 0;

  const isLoading = useIsLoading([isTagTypesLoading]);
  const [auditLogOpen, setAuditLogOpen] = useState(false);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);

  const updateTagOrders = useUpdateTagOrdersMutation();
  const updateTagTypeOrders = useUpdateTagTypeOrdersMutation();

  const {
    tagTypesContainerRef,
    setRef,
    tagTypeJoinerCoordinates,
    recalculateCoordinates,
  } = useTagsTypesCoordinates({ orderedTagTypes: tagTypes, draggingId });

  const onDragStart = ({ draggableId, type }: DragStart) => {
    if (type === 'parent') {
      setDraggingId(draggableId);
    }
  };

  const onDragEnd = (result: DropResult) => {
    setDraggingId(null);

    const { destination, type, source } = result;
    if (!destination) return;

    if (!tagTypes) return;

    if (type === 'parent') {
      const reorderedTagTypes = Array.from(tagTypes);
      const [reorderedItem] = reorderedTagTypes.splice(result.source.index, 1);
      reorderedTagTypes.splice(destination.index, 0, reorderedItem);

      const updatedTagTypes = reorderedTagTypes.map((tagType, index) => ({
        ...tagType,
        order: index,
      }));

      dispatch(setGlobalTagTypes(updatedTagTypes));

      updateTagTypeOrders
        .mutateAsync({
          tagTypes: updatedTagTypes.map((tagType) => ({
            id: tagType.id,
            order: tagType.order,
          })),
          draggedTagTypeId: reorderedItem.id,
        })
        .then(() => {
          invalidateQuery();
        });
    } else {
      // Reorder child components within a specific parent
      const tagTypeIndex = tagTypes.findIndex(
        (parent) => parent.id === source.droppableId,
      );

      const Tags = tagTypes[tagTypeIndex].Tags;
      if (!Tags) return;

      const reorderedTags = Array.from(Tags);

      const [reorderedItem] = reorderedTags.splice(source.index, 1);
      reorderedTags.splice(destination.index, 0, reorderedItem);

      // Update the order for each tagType
      const updatedTags = reorderedTags.map((tag, index) => ({
        ...tag,
        order: index,
      }));

      const updatedData = Array.from(tagTypes);
      updatedData[tagTypeIndex] = {
        ...updatedData[tagTypeIndex],
        Tags: updatedTags,
      };

      dispatch(setGlobalTagTypes(updatedData));

      updateTagOrders
        .mutateAsync({
          tags: updatedTags.map((tag) => ({
            tagId: tag.id,
            order: tag.order,
          })),
          draggedTagId: reorderedItem.id,
        })
        .then(() => {
          invalidateQuery();
        });
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <PageContainer data-testid="tags-page">
      <BodyContainer>
        <PageHeaderContainer>
          <PageHeaderWithActions
            title="Tags"
            openAuditLog={() => setAuditLogOpen(true)}
          />
          {!noTagTypes && (
            <OrderInfoContainer>
              <OrderInfoSubtitle>Order</OrderInfoSubtitle>
              <OrderInfo
                className="uil uil-info-circle"
                size="24px"
                onClick={() => setOrderModalOpen(true)}
              />
            </OrderInfoContainer>
          )}
        </PageHeaderContainer>

        {noTagTypes ? (
          <EmptyStateWrapper>
            <EmptyState
              data-testid="tags-configuration-empty-state"
              description="Tag types will show here prior to launch of Vault at your organization. Please reach out to our technical support team should they not appear here at time of launch."
              title="Welcome to Tags"
              notificationEmptyState={true}
            />
          </EmptyStateWrapper>
        ) : (
          <div ref={tagTypesContainerRef}>
            <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
              <Droppable droppableId="tagTypes" type="parent">
                {(provided) => (
                  <TagsBody
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    <GridContainer>
                      <div>
                        {tagTypes?.map((type, index) => (
                          <Draggable
                            key={type.id}
                            draggableId={type.id}
                            index={index}
                          >
                            {(provided) => (
                              <TagContainer
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <TagTypeItem
                                  key={index}
                                  index={index}
                                  dragHandleProps={provided.dragHandleProps}
                                  type={type as TagTypeType}
                                  droppableId={type.id}
                                  recalculateCoordinates={
                                    recalculateCoordinates
                                  }
                                  tagTypeJoinerCoordinates={
                                    tagTypeJoinerCoordinates
                                  }
                                  setRef={setRef}
                                  isDragging={type.id === draggingId}
                                />
                              </TagContainer>
                            )}
                          </Draggable>
                        ))}
                      </div>

                      <div>
                        <>
                          <TagsSummary
                            orderedTagTypes={tagTypes as TagTypeType[]}
                            orderedTags={tagTypes
                              .map((tagType) => tagType.Tags)
                              .flat()}
                          />
                          <SuggestedReading articles={tagsIntercomArticle} />
                        </>
                      </div>
                      <TagsAuditLogTable
                        isOpen={auditLogOpen}
                        setIsOpen={setAuditLogOpen}
                      />
                    </GridContainer>
                    {provided.placeholder}
                  </TagsBody>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </BodyContainer>
      <Modal
        size="medium"
        title="Order tag types"
        description={
          <>
            <OrderParagraph>
              {`The order you set for the ${tagTypes.length} tag types determines how they appear
              to case managers. Drag and drop the tag types into your preferred
              order. The changes will be saved automatically, helping case
              managers find and apply tags more efficiently.`}
            </OrderParagraph>
          </>
        }
        open={orderModalOpen}
        setOpen={setOrderModalOpen}
        onConfirm={() => setOrderModalOpen(false)}
        confirmButtonText="OK"
      />
    </PageContainer>
  );
};
